import isNil from "lodash/isNil";
import { useBookingPastDaysLimit } from "PFApp/hooks/use_booking_past_days_limit";
import { utc } from "PFCore/helpers/date";
import { FormatFunction } from "PFCore/helpers/date_formatter";
import { Activity, ActivityOption, Id } from "PFTypes";
import { useEffect, useRef } from "react";
import { UseFormSetValue } from "react-hook-form";

import { BookingFormMode } from "../booking_form.types";
import { BookingFormValues } from "../booking_form_values.types";

type UseEngagementDurationSyncParams = {
  activity: Activity | undefined;
  selectedActivity: Activity | undefined;
  isInitialActivityEngagement: boolean | undefined;
  hasInitialBookingDates: boolean;
  mode: BookingFormMode;
  activityId: BookingFormValues["activityId"];
  bookings: BookingFormValues["bookings"];
  setValue: UseFormSetValue<BookingFormValues>;
  formatISODate: FormatFunction;
};

export const useEngagementDurationSync = ({
  activity,
  selectedActivity,
  isInitialActivityEngagement,
  hasInitialBookingDates,
  mode,
  activityId,
  bookings,
  setValue,
  formatISODate
}: UseEngagementDurationSyncParams) => {
  const bookingPastDaysLimit = useBookingPastDaysLimit();
  const pendingDatesActivityId = useRef<Id | null>(null);

  const handleEngagementChange = (engagementOption: ActivityOption | null) => {
    if (!engagementOption) {
      pendingDatesActivityId.current = null;
      if (bookings?.length === 1) {
        const today = formatISODate(utc());

        setValue(`bookings.0.startDate`, today);
        setValue(`bookings.0.endDate`, today);
        setValue(`bookings.0.withTime`, false);
      }
    } else if (engagementOption.id !== activityId) {
      pendingDatesActivityId.current = engagementOption.id;
    }
  };

  useEffect(() => {
    if (isInitialActivityEngagement && mode === BookingFormMode.Create && !hasInitialBookingDates) {
      pendingDatesActivityId.current = activity?.id ?? null;
    }
  }, [isInitialActivityEngagement]);

  useEffect(() => {
    const pendingId = pendingDatesActivityId.current;

    if (isNil(pendingId) || !selectedActivity || Number(selectedActivity.id) !== Number(pendingId)) {
      return;
    }
    if (!bookings?.length) {
      return;
    }

    pendingDatesActivityId.current = null;

    if (bookings.length !== 1) {
      return;
    }

    const duration = selectedActivity.metadata?.duration;

    if (duration?.start_date && duration?.end_date) {
      const earliestAllowedDate = utc().subtract(bookingPastDaysLimit, "days").startOf("day");
      const durationStartDate = utc(duration.start_date).startOf("day");
      const durationEndDate = utc(duration.end_date).startOf("day");

      if (durationEndDate.isBefore(earliestAllowedDate)) {
        return;
      }

      const startDate = durationStartDate.isBefore(earliestAllowedDate)
        ? earliestAllowedDate
        : durationStartDate;

      setValue(`bookings.0.startDate`, formatISODate(startDate));
      setValue(`bookings.0.endDate`, formatISODate(durationEndDate));
      setValue(`bookings.0.withTime`, false);
    }
  }, [selectedActivity, bookings?.length]);

  return { handleEngagementChange };
};
